import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Recycle, Sparkles } from "lucide-react";
import { SearchBar } from "../components/site/Header";
import AdCard, { AdSkeleton } from "../components/site/AdCard";
import { http } from "../lib/api";
import { CATEGORIES, CATEGORY_INFO, QUICK_DISTRICTS } from "../lib/categories";
import PropertyHighlight from "../components/site/PropertyHighlight";

function BigButtons() {
  return (
    <div className="grid sm:grid-cols-2 gap-4 mt-6">
      <Link to="/sell" className="rise flex items-center justify-center gap-3 rounded-2xl bg-[var(--sell)] hover:bg-[#c62828] text-white px-6 py-5 md:py-6 shadow-lg shadow-red-500/25 hover:-translate-y-0.5 transition-[transform,background-color]" data-testid="home-sell-button">
        <span className="text-2xl">🔴</span>
        <span className="font-display font-bold text-xl md:text-2xl">विकायचे आहे - जाहिरात टाका</span>
      </Link>
      <Link to="/buy" className="rise flex items-center justify-center gap-3 rounded-2xl bg-[var(--buy)] hover:bg-[#0d47a1] text-white px-6 py-5 md:py-6 shadow-lg shadow-blue-500/25 hover:-translate-y-0.5 transition-[transform,background-color]" style={{ animationDelay: ".08s" }} data-testid="home-buy-button">
        <span className="text-2xl">🔵</span>
        <span className="font-display font-bold text-xl md:text-2xl">विकत घ्यायचे आहे - खरेदी करा</span>
      </Link>
    </div>
  );
}

function CategoryGrid({ counts }) {
  return (
    <section className="max-w-7xl mx-auto px-4 mt-12">
      <h2 className="font-display font-bold text-2xl text-[var(--navy)]">Browse Categories · <span className="text-[var(--orange)]">श्रेणी</span></h2>
      <div className="mt-5 grid grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
        {CATEGORIES.map((c, i) => {
          const { icon: Icon, mr, color, bg } = CATEGORY_INFO[c];
          return (
            <Link key={c} to={`/buy?category=${c}`} className="rise group flex flex-col items-center text-center gap-2 rounded-2xl bg-white border border-slate-200 p-3 md:p-4 hover:border-[var(--orange)] hover:shadow-md transition-[box-shadow,border-color]" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`category-card-${c.toLowerCase()}`}>
              <span className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform" style={{ background: bg, color }}>
                <Icon className="w-6 h-6 md:w-7 md:h-7" />
              </span>
              <span className="text-xs md:text-sm font-semibold text-[var(--navy)] leading-tight">{c}<span className="block text-slate-500 font-normal">{mr}</span></span>
              {counts?.[c] ? <span className="hidden md:block text-[11px] text-slate-400">{counts[c]} {counts[c] === 1 ? "ad" : "ads"}</span> : null}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function SuccessCounter({ sold, live }) {
  const stats = [
    { v: sold, l: "डील्स थेट झाल्या", s: "Deals done without broker", cls: "text-green-700", id: "home-sold-count" },
    { v: 0, l: "₹ कमिशन दिले", s: "Commission paid", cls: "text-[var(--orange)]", id: "home-commission", prefix: "₹" },
    { v: live, l: "Live जाहिराती", s: "Active ads", cls: "text-[var(--buy)]", id: "home-live-count" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 mt-8" data-testid="success-counter">
      <div className="rounded-2xl bg-white border-2 border-green-600 grid grid-cols-3 divide-x divide-slate-200">
        {stats.map(({ v, l, s, cls, id, prefix }) => (
          <div key={id} className="p-4 md:p-6 text-center">
            <p className={`font-display font-extrabold text-3xl md:text-5xl ${cls}`} data-testid={id}>{v === null || v === undefined ? "—" : `${prefix || ""}${Number(v).toLocaleString("en-IN")}`}</p>
            <p className="font-semibold text-[var(--navy)] text-sm md:text-base mt-1">{l}</p>
            <p className="text-[11px] md:text-xs text-slate-500">{s}</p>
          </div>
        ))}
      </div>
      <p className="mt-2 text-center text-xs md:text-sm font-semibold text-green-700">{sold === 0 ? "पहिली थेट डील तुमची होऊ दे! वस्तू विकल्यावर My Ads मध्ये SOLD करा." : "नो ब्रोकर - नो दलाल - खरेदीदार आणि विक्रेता थेट भेटले!"}</p>
    </section>
  );
}

function BazaarSplit() {
  const cards = [
    { to: "/buy?condition=old", title: "जुना बाजार", en: "Juna Bazaar · Used Items", text: "वापरलेल्या वस्तू स्वस्तात - Mobile, Bike, Furniture...", icon: Recycle, cls: "bg-[#FFF0E0] border-[#FFD3A6]", accent: "text-[var(--orange)]", id: "old" },
    { to: "/buy?condition=new", title: "नवीन बाजार", en: "Navin Bazaar · Brand New", text: "नवीन वस्तू, नवीन फ्लॅट, दुकानदारांच्या ऑफर्स", icon: Sparkles, cls: "bg-[#E3EEFC] border-[#BCD3F5]", accent: "text-[var(--buy)]", id: "new" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 mt-10 grid sm:grid-cols-2 gap-4">
      {cards.map(({ to, title, en, text, icon: Icon, cls, accent, id }) => (
        <Link key={id} to={to} className={`group rounded-2xl border-2 p-6 flex items-center gap-5 hover:shadow-lg transition-shadow ${cls}`} data-testid={`home-bazaar-${id}`}>
          <span className={`w-16 h-16 rounded-2xl bg-white flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform ${accent}`}><Icon className="w-8 h-8" /></span>
          <span>
            <span className={`block font-display font-extrabold text-2xl md:text-3xl ${accent}`}>{title}</span>
            <span className="block text-sm font-semibold text-[var(--navy)]">{en}</span>
            <span className="block text-sm text-slate-600 mt-1">{text}</span>
          </span>
          <ArrowRight className={`w-6 h-6 ml-auto shrink-0 ${accent} group-hover:translate-x-1 transition-transform`} />
        </Link>
      ))}
    </section>
  );
}

export default function Home() {
  const [ads, setAds] = useState(null);
  const [counts, setCounts] = useState(null);
  const [ptypeCounts, setPtypeCounts] = useState(null);
  const [sold, setSold] = useState(null);
  useEffect(() => {
    http.get("/ads", { params: { limit: 12 } }).then((r) => setAds(r.data.items)).catch(() => setAds([]));
    http.get("/meta").then((r) => { setCounts(r.data.counts); setPtypeCounts(r.data.ptype_counts); setSold(r.data.sold_count); }).catch(() => {});
  }, []);

  return (
    <div>
      <section className="relative bg-white border-b border-slate-200 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#FFE7CF]" />
        <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-[#E3EEFC]" />
        <div className="relative max-w-4xl mx-auto px-4 py-10 md:py-14">
          <p className="rise text-sm font-semibold text-[var(--orange)] tracking-wide flex items-center gap-1"><MapPin className="w-4 h-4" /> महाराष्ट्रातील ३६ जिल्हे · All 36 districts</p>
          <h1 className="rise mt-2 font-display font-extrabold text-4xl sm:text-5xl text-[var(--navy)] leading-tight">आपला बाजार, <span className="text-[var(--orange)]">आपल्या गावात.</span></h1>
          <p className="rise mt-2 text-slate-600 text-base md:text-lg">Buy & sell mobiles, cars, bikes, property, jobs and more — direct, no broker.</p>
          <div className="rise mt-6"><SearchBar big testId="home-search" /></div>
          <BigButtons />
          <div className="mt-5 flex flex-wrap gap-2 text-sm">
            {QUICK_DISTRICTS.map((d) => (
              <Link key={d} to={`/buy?location=${encodeURIComponent(d)}`} className="px-3 py-1 rounded-full bg-white border border-slate-200 hover:border-[var(--buy)] hover:text-[var(--buy)] transition-colors" data-testid={`home-district-${d.toLowerCase().replace(/\s+/g, "-")}`}>{d}</Link>
            ))}
          </div>
        </div>
      </section>

      <SuccessCounter sold={sold} live={counts ? Object.values(counts).reduce((a, b) => a + b, 0) : null} />

      <PropertyHighlight counts={ptypeCounts} />

      <CategoryGrid counts={counts} />

      <BazaarSplit />

      <section className="max-w-7xl mx-auto px-4 mt-12">
        <div className="flex items-end justify-between">
          <h2 className="font-display font-bold text-2xl text-[var(--navy)]">Latest Ads · <span className="text-[var(--orange)]">नवीन जाहिराती</span></h2>
          <Link to="/buy" className="text-sm font-semibold text-[var(--buy)] flex items-center gap-1 hover:gap-2 transition-[gap]" data-testid="home-view-all-link">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-testid="latest-ads-grid">
          {ads ? ads.map((ad) => <AdCard key={ad.id} ad={ad} />) : Array.from({ length: 8 }).map((_, i) => <AdSkeleton key={i} />)}
        </div>
        {ads && ads.length === 0 && <p className="text-center text-slate-500 py-10" data-testid="home-no-ads">अजून जाहिराती नाहीत. पहिली जाहिरात टाका!</p>}
      </section>
    </div>
  );
}
